import React, { useState, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { Search, ChevronDown, ChevronRight } from 'react-feather';
import { AlertTriangle, Check, ChevronLeft, ChevronRight as ChevronRightLucide, Loader2 } from 'lucide-react';
import { InlineSelect } from './InlineTable';
import { formatCurrencyRounded, currencySymbol } from '../utils/currency';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatCurrency(value, currency) {
  if (!value && value !== 0) return `${currencySymbol(currency)}0`;
  return formatCurrencyRounded(Math.abs(value), currency);
}

// Month dates from the API are UTC instants ("2026-08-01T00:00:00.000Z"); reading one
// with local getMonth() slides an August row into July west of UTC. Every other budget
// screen buckets on the UTC date part (see BudgetTable.parseBudgetEntryDate) — match
// that here or the month navigator shows the neighbouring month's dollars.
function utcMonthParts(value) {
  if (!value) return null;
  const iso = typeof value === 'string' ? value : new Date(value).toISOString();
  const [year, month] = iso.slice(0, 10).split('-');
  const monthIdx = parseInt(month, 10) - 1;
  const parsedYear = parseInt(year, 10);
  if (Number.isNaN(parsedYear) || Number.isNaN(monthIdx) || monthIdx < 0 || monthIdx > 11) return null;
  return { year: parsedYear, monthIdx, label: `${MONTH_SHORT[monthIdx]} ${parsedYear}` };
}

// The revenueType a budget account points at only carries its dollars into the
// dashboard/report facts when it is live AND has a real mappedType — the fact CTE
// joins `rt.isDeleted = 0 AND rt.mappedType IS NOT NULL AND rt.mappedType != 'DNI'`.
// Anything else looks mapped here but silently drops the budget, so name it.
function describeMapping(mapping) {
  if (!mapping) return null;
  if (mapping.typeMissing) {
    return { label: `Type #${mapping.revenueTypeId} not found`, tone: 'danger', excluded: true };
  }
  if (mapping.typeIsDeleted) {
    return { label: 'Type deleted', tone: 'danger', excluded: true };
  }
  const mappedType = (mapping.mappedType || '').trim();
  if (!mappedType) return { label: 'No mapped type', tone: 'warn', excluded: true };
  if (mappedType.toUpperCase() === 'DNI') return { label: 'DNI', tone: 'warn', excluded: true };
  return { label: mappedType, tone: 'neutral', excluded: false };
}

const MAPPED_TYPE_CHIP_CLASSES = {
  neutral: 'text-gray-500 bg-gray-100',
  warn: 'text-amber-700 bg-amber-100',
  danger: 'text-red-700 bg-red-100',
};

const EXCLUDED_CHIP_TITLE =
  'Budget on this account is excluded from revenue reporting — the mapped revenue type is deleted, missing, or has no usable mapped type (blank or DNI).';

const BudgetCoaMappingView = ({
  chartAccounts = [],
  revenueTypes = [],
  budgetMappingsData = {},
  financialBudgets = [],
  accountActivity = [],
  selectedYear,
  onYearChange,
  orgId,
  locationId,
  currency,
  onCreateMapping,
  onDeleteMapping,
  loading = false,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-indexed
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'unmappedWithBudget'
  const [collapsedDepartments, setCollapsedDepartments] = useState(new Set());
  const [savingRows, setSavingRows] = useState(new Set());

  // Reverse lookup: chartOfAccountId -> mapping info
  const coaToRevenueTypeMap = useMemo(() => {
    const map = {};
    Object.entries(budgetMappingsData).forEach(([revenueTypeId, mappings]) => {
      if (!Array.isArray(mappings)) return;
      const rt = revenueTypes.find(r => r.id === parseInt(revenueTypeId));
      mappings.forEach(mapping => {
        map[mapping.chartOfAccountId] = {
          revenueTypeId: parseInt(revenueTypeId),
          revenueTypeName: rt?.name || `Revenue Type #${revenueTypeId}`,
          mappingId: mapping.id,
          mappedType: rt?.mappedType || null,
          typeIsDeleted: !!rt?.isDeleted,
          typeMissing: !rt,
        };
      });
    });
    return map;
  }, [budgetMappingsData, revenueTypes]);

  // Aggregate budget amounts by COA + month
  const budgetByCoaMonth = useMemo(() => {
    const map = {};
    financialBudgets.forEach(b => {
      if (!b.chartOfAccountId || !b.budgetDate) return;
      const parts = utcMonthParts(b.budgetDate);
      if (!parts || parts.year !== selectedYear) return;
      const key = `${b.chartOfAccountId}-${parts.monthIdx}`;
      map[key] = (map[key] || 0) + parseFloat(b.budgetAmount || 0);
    });
    return map;
  }, [financialBudgets, selectedYear]);

  // Last non-zero GL month per COA (decoration — empty when the location has no GL feed)
  const activityByCoa = useMemo(() => {
    const map = {};
    (accountActivity || []).forEach(row => {
      if (!row || row.chartOfAccountId == null) return;
      const parsed = utcMonthParts(row.lastActivityMonth);
      if (!parsed) return;
      map[row.chartOfAccountId] = { ...parsed, amount: Number(row.lastActivityAmount) || 0 };
    });
    return map;
  }, [accountActivity]);

  const hasActivityData = Object.keys(activityByCoa).length > 0;

  // Revenue COAs only, sorted by department then account number
  const revenueCoas = useMemo(() => {
    return chartAccounts
      .filter(a => {
        const type = (a.accountType || '').toLowerCase();
        return type === 'revenue' && !a.isDeleted;
      })
      .sort((a, b) => {
        const deptCmp = (a.department || '').localeCompare(b.department || '');
        if (deptCmp !== 0) return deptCmp;
        return (a.accountNumber || '').localeCompare(b.accountNumber || '');
      });
  }, [chartAccounts]);

  // Apply search + filter
  const filteredCoas = useMemo(() => {
    let result = revenueCoas;

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(coa =>
        (coa.accountNumber || '').toLowerCase().includes(term) ||
        (coa.accountName || '').toLowerCase().includes(term)
      );
    }

    // Filter: unmapped with budget
    if (filterMode === 'unmappedWithBudget') {
      result = result.filter(coa => {
        const budget = budgetByCoaMonth[`${coa.id}-${selectedMonth}`] || 0;
        const isMapped = !!coaToRevenueTypeMap[coa.id];
        return !isMapped && Math.abs(budget) > 0;
      });
    }

    // Filter: mapped to a revenue type whose dollars never reach reporting
    if (filterMode === 'excludedWithBudget') {
      result = result.filter(coa => {
        const budget = budgetByCoaMonth[`${coa.id}-${selectedMonth}`] || 0;
        return describeMapping(coaToRevenueTypeMap[coa.id])?.excluded && Math.abs(budget) > 0;
      });
    }

    return result;
  }, [revenueCoas, searchTerm, filterMode, budgetByCoaMonth, selectedMonth, coaToRevenueTypeMap]);

  // Group by department
  const coasByDepartment = useMemo(() => {
    const groups = {};
    filteredCoas.forEach(coa => {
      const dept = coa.department || 'Uncategorized';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(coa);
    });
    return groups;
  }, [filteredCoas]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const totalRevenueCoas = revenueCoas.length;
    const mappedCount = revenueCoas.filter(c => !!coaToRevenueTypeMap[c.id]).length;
    const unmappedCount = totalRevenueCoas - mappedCount;

    let unmappedBudgetTotal = 0;
    let totalBudget = 0;
    revenueCoas.forEach(coa => {
      const budget = budgetByCoaMonth[`${coa.id}-${selectedMonth}`] || 0;
      totalBudget += Math.abs(budget);
      if (!coaToRevenueTypeMap[coa.id]) {
        unmappedBudgetTotal += Math.abs(budget);
      }
    });

    // Count unmapped with budget specifically
    const unmappedWithBudgetCount = revenueCoas.filter(coa => {
      const budget = budgetByCoaMonth[`${coa.id}-${selectedMonth}`] || 0;
      return !coaToRevenueTypeMap[coa.id] && Math.abs(budget) > 0;
    }).length;

    // Mapped, but to a type the fact build throws away (deleted / missing / blank / DNI)
    let excludedBudgetTotal = 0;
    const excludedWithBudgetCount = revenueCoas.filter(coa => {
      const budget = budgetByCoaMonth[`${coa.id}-${selectedMonth}`] || 0;
      const isExcluded = !!describeMapping(coaToRevenueTypeMap[coa.id])?.excluded;
      if (isExcluded && Math.abs(budget) > 0) {
        excludedBudgetTotal += Math.abs(budget);
        return true;
      }
      return false;
    }).length;

    return {
      totalRevenueCoas, mappedCount, unmappedCount, unmappedBudgetTotal, totalBudget,
      unmappedWithBudgetCount, excludedWithBudgetCount, excludedBudgetTotal,
    };
  }, [revenueCoas, coaToRevenueTypeMap, budgetByCoaMonth, selectedMonth]);

  // Revenue type options for dropdown — live types only. The hint carries each type's
  // mappedType so you can see which bucket a choice lands in before picking it.
  const revenueTypeOptions = useMemo(() => {
    return revenueTypes
      .filter(rt => !rt.isDeleted)
      .map(rt => {
        const mappedType = (rt.mappedType || '').trim();
        return {
          value: rt.id,
          label: rt.name,
          hint: !mappedType ? 'No mapped type' : mappedType,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [revenueTypes]);

  // A mapping can point at a soft-deleted or missing revenue type; without an option
  // to match, InlineSelect falls back to its placeholder and the row reads as unmapped
  // while the Status column says "Mapped". Append the stale type so the row tells the truth.
  const optionsForMapping = useMemo(() => {
    const cache = new Map();
    return (mapping) => {
      if (!mapping) return revenueTypeOptions;
      if (revenueTypeOptions.some(o => o.value === mapping.revenueTypeId)) return revenueTypeOptions;
      if (!cache.has(mapping.revenueTypeId)) {
        cache.set(mapping.revenueTypeId, [
          {
            value: mapping.revenueTypeId,
            label: `${mapping.revenueTypeName}${mapping.typeMissing ? ' (missing)' : ' (deleted)'}`,
          },
          ...revenueTypeOptions,
        ]);
      }
      return cache.get(mapping.revenueTypeId);
    };
  }, [revenueTypeOptions]);

  // Month navigation
  const handlePrevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      onYearChange?.(selectedYear - 1);
    } else {
      setSelectedMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      onYearChange?.(selectedYear + 1);
    } else {
      setSelectedMonth(prev => prev + 1);
    }
  };

  // Toggle department collapse
  const toggleDepartment = (dept) => {
    setCollapsedDepartments(prev => {
      const next = new Set(prev);
      if (next.has(dept)) next.delete(dept);
      else next.add(dept);
      return next;
    });
  };

  // Handle mapping change for a COA
  const handleMappingChange = async (chartOfAccountId, selectedOption) => {
    const existing = coaToRevenueTypeMap[chartOfAccountId];

    setSavingRows(prev => new Set(prev).add(chartOfAccountId));

    try {
      // Remove existing mapping if any
      if (existing) {
        await onDeleteMapping(existing.mappingId, existing.revenueTypeId);
      }

      // Create new mapping if selected (not cleared)
      if (selectedOption) {
        await onCreateMapping(selectedOption.value, chartOfAccountId);
      }
    } catch (error) {
      console.error('Error updating mapping:', error);
      toast.error('Failed to update mapping');
    } finally {
      setSavingRows(prev => {
        const next = new Set(prev);
        next.delete(chartOfAccountId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-orange-500 mb-3" />
        <p className="text-sm text-gray-500">Loading budget data...</p>
      </div>
    );
  }

  const departmentNames = Object.keys(coasByDepartment).sort();

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {/* Month/Year Navigator */}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg px-1 py-1">
            <button
              onClick={handlePrevMonth}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronLeft size={14} className="text-gray-600" />
            </button>
            <span className="text-sm font-medium text-gray-800 min-w-[100px] text-center">
              {MONTH_SHORT[selectedMonth]} {selectedYear}
            </span>
            <button
              onClick={handleNextMonth}
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            >
              <ChevronRightLucide size={14} className="text-gray-600" />
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by account # or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-56 pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Filter toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setFilterMode('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterMode === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All <span className="ml-1 text-[10px] font-semibold">{revenueCoas.length}</span>
            </button>
            <button
              onClick={() => setFilterMode('unmappedWithBudget')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterMode === 'unmappedWithBudget'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : summaryStats.unmappedWithBudgetCount > 0
                    ? 'text-amber-600 hover:text-amber-700'
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Unmapped w/ Budget <span className="ml-1 text-[10px] font-semibold">{summaryStats.unmappedWithBudgetCount}</span>
            </button>
            {summaryStats.excludedWithBudgetCount > 0 && (
              <button
                onClick={() => setFilterMode('excludedWithBudget')}
                title={EXCLUDED_CHIP_TITLE}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  filterMode === 'excludedWithBudget'
                    ? 'bg-white text-red-700 shadow-sm'
                    : 'text-red-600 hover:text-red-700'
                }`}
              >
                Excluded w/ Budget <span className="ml-1 text-[10px] font-semibold">{summaryStats.excludedWithBudgetCount}</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Summary Stats Bar */}
      {summaryStats.excludedWithBudgetCount > 0 && (
        <div className="mb-4 flex items-center justify-between px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-800">
              <span className="font-semibold text-red-900">{summaryStats.excludedWithBudgetCount}</span> mapped COA{summaryStats.excludedWithBudgetCount !== 1 ? 's' : ''} with
              {' '}<span className="font-semibold text-red-900">{formatCurrency(summaryStats.excludedBudgetTotal, currency)}</span> in budget point at a revenue type that is deleted, missing, or has no usable mapped type — those dollars never reach reporting.
            </p>
          </div>
        </div>
      )}
      {summaryStats.unmappedWithBudgetCount > 0 ? (
        <div className="mb-4 flex items-center justify-between px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-3">
            <AlertTriangle size={16} className="text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-semibold text-amber-900">{summaryStats.unmappedWithBudgetCount}</span> COA{summaryStats.unmappedWithBudgetCount !== 1 ? 's' : ''} with
              {' '}<span className="font-semibold text-amber-900">{formatCurrency(summaryStats.unmappedBudgetTotal, currency)}</span> in budget for {MONTH_NAMES[selectedMonth]} {selectedYear} {summaryStats.unmappedWithBudgetCount !== 1 ? 'are' : 'is'} unmapped.
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {summaryStats.mappedCount} of {summaryStats.totalRevenueCoas} mapped
          </div>
        </div>
      ) : summaryStats.excludedWithBudgetCount === 0 ? (
        <div className="mb-4 flex items-center justify-between px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Check size={16} className="text-emerald-500 flex-shrink-0" />
            <p className="text-sm text-emerald-800">
              All revenue COAs with budget amounts are mapped.
            </p>
          </div>
          <div className="text-sm text-gray-500">
            {summaryStats.mappedCount} of {summaryStats.totalRevenueCoas} mapped
          </div>
        </div>
      ) : null}

      {/* Table */}
      {filteredCoas.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <p className="text-sm">
            {searchTerm || filterMode !== 'all'
              ? 'No matching COA accounts found.'
              : 'No revenue chart of accounts found.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-8"></th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-24">Account #</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Account Name</th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-40">
                  Budget ({MONTH_SHORT[selectedMonth]} {selectedYear})
                </th>
                <th
                  className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-36"
                  title="Most recent month with non-zero net GL activity on this account"
                >
                  Last Revenue
                </th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-72">Revenue Type</th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-16">Status</th>
              </tr>
            </thead>
            <tbody>
              {departmentNames.map(dept => {
                const deptCoas = coasByDepartment[dept];
                const isCollapsed = collapsedDepartments.has(dept);
                const deptUnmappedCount = deptCoas.filter(c => !coaToRevenueTypeMap[c.id]).length;
                const deptUnmappedWithBudget = deptCoas.filter(c => {
                  const budget = budgetByCoaMonth[`${c.id}-${selectedMonth}`] || 0;
                  return !coaToRevenueTypeMap[c.id] && Math.abs(budget) > 0;
                }).length;
                const deptExcludedWithBudget = deptCoas.filter(c => {
                  const budget = budgetByCoaMonth[`${c.id}-${selectedMonth}`] || 0;
                  return describeMapping(coaToRevenueTypeMap[c.id])?.excluded && Math.abs(budget) > 0;
                }).length;

                return (
                  <React.Fragment key={dept}>
                    {/* Department Header */}
                    <tr
                      className="bg-gray-50 border-t border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => toggleDepartment(dept)}
                    >
                      <td colSpan={7} className="px-4 py-2.5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isCollapsed ? (
                              <ChevronRight size={14} className="text-gray-400" />
                            ) : (
                              <ChevronDown size={14} className="text-gray-400" />
                            )}
                            <span className="text-sm font-semibold text-gray-700">{dept}</span>
                            <span className="text-xs text-gray-400">
                              {deptCoas.length} account{deptCoas.length !== 1 ? 's' : ''}
                            </span>
                            {deptUnmappedWithBudget > 0 && (
                              <span className="text-[10px] font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                                {deptUnmappedWithBudget} unmapped w/ budget
                              </span>
                            )}
                            {deptExcludedWithBudget > 0 && (
                              <span
                                className="text-[10px] font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded"
                                title={EXCLUDED_CHIP_TITLE}
                              >
                                {deptExcludedWithBudget} excluded w/ budget
                              </span>
                            )}
                          </div>
                          {deptUnmappedCount > 0 && (
                            <span className="text-[10px] text-gray-400">
                              {deptCoas.length - deptUnmappedCount}/{deptCoas.length} mapped
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* COA Rows */}
                    {!isCollapsed && deptCoas.map(coa => {
                      const budgetAmount = budgetByCoaMonth[`${coa.id}-${selectedMonth}`] || 0;
                      const mapping = coaToRevenueTypeMap[coa.id];
                      const isMapped = !!mapping;
                      const hasBudget = Math.abs(budgetAmount) > 0;
                      const isUnmappedWithBudget = !isMapped && hasBudget;
                      const isSaving = savingRows.has(coa.id);
                      const mappingInfo = describeMapping(mapping);
                      const isExcluded = !!mappingInfo?.excluded;
                      const isExcludedWithBudget = isExcluded && hasBudget;
                      const activity = activityByCoa[coa.id];
                      const monthsSinceActivity = activity
                        ? (selectedYear - activity.year) * 12 + (selectedMonth - activity.monthIdx)
                        : null;
                      const isStaleActivity = monthsSinceActivity != null && monthsSinceActivity > 12;

                      const currentValue = mapping ? mapping.revenueTypeId : null;

                      return (
                        <tr
                          key={coa.id}
                          className={`border-b border-gray-100 transition-colors ${
                            isExcludedWithBudget
                              ? 'bg-red-50/50 border-l-[3px] border-l-red-400'
                              : isUnmappedWithBudget
                                ? 'bg-amber-50/50 border-l-[3px] border-l-amber-400'
                                : isMapped
                                  ? 'border-l-[3px] border-l-emerald-400'
                                  : 'border-l-[3px] border-l-transparent'
                          } ${isSaving ? 'opacity-60' : ''}`}
                        >
                          {/* Status Icon */}
                          <td className="px-4 py-2.5 text-center">
                            {isUnmappedWithBudget && (
                              <AlertTriangle size={14} className="text-amber-500" />
                            )}
                            {isMapped && (
                              isExcluded
                                ? <AlertTriangle size={14} className="text-red-500" />
                                : <Check size={14} className="text-emerald-500" />
                            )}
                          </td>
                          {/* Account # */}
                          <td className="px-4 py-2.5 text-sm font-mono text-gray-700 whitespace-nowrap">
                            {coa.accountNumber}
                          </td>
                          {/* Account Name */}
                          <td className="px-4 py-2.5 text-sm text-gray-800">
                            {coa.accountName}
                          </td>
                          {/* Budget Amount */}
                          <td className={`px-4 py-2.5 text-sm text-right tabular-nums whitespace-nowrap ${
                            hasBudget ? 'text-gray-800 font-medium' : 'text-gray-300'
                          }`}>
                            {hasBudget ? formatCurrency(budgetAmount, currency) : formatCurrency(0, currency)}
                          </td>
                          {/* Last non-zero GL month */}
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {activity ? (
                              <div title={`Net ${formatCurrency(activity.amount, currency)} in ${activity.label}`}>
                                <div className={`text-sm tabular-nums ${isStaleActivity ? 'text-amber-600' : 'text-gray-700'}`}>
                                  {activity.label}
                                </div>
                                <div className="text-[11px] text-gray-400 tabular-nums">
                                  {formatCurrency(activity.amount, currency)}
                                </div>
                              </div>
                            ) : (
                              <span
                                className="text-sm text-gray-300"
                                title={hasActivityData
                                  ? 'No non-zero GL activity recorded for this account'
                                  : 'No GL activity data for this location'}
                              >
                                —
                              </span>
                            )}
                          </td>
                          {/* Revenue Type Dropdown */}
                          <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <InlineSelect
                              value={currentValue}
                              options={optionsForMapping(mapping)}
                              onChange={(v) => handleMappingChange(coa.id, v != null ? { value: v } : null)}
                              placeholder="Select revenue type..."
                              rowActive
                              disabled={isSaving}
                              minWidth={260}
                              ariaLabel="Revenue type"
                            />
                            {mappingInfo && (
                              <div className="mt-1 pl-2">
                                <span
                                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${MAPPED_TYPE_CHIP_CLASSES[mappingInfo.tone]}`}
                                  title={mappingInfo.excluded ? EXCLUDED_CHIP_TITLE : `Mapped type: ${mappingInfo.label}`}
                                >
                                  {mappingInfo.label}
                                </span>
                              </div>
                            )}
                          </td>
                          {/* Status label */}
                          <td className="px-4 py-2.5 text-center">
                            {isSaving ? (
                              <Loader2 size={14} className="animate-spin text-gray-400 mx-auto" />
                            ) : isExcluded ? (
                              <span
                                className="text-[10px] font-semibold text-red-600 bg-red-100 px-1.5 py-0.5 rounded"
                                title={EXCLUDED_CHIP_TITLE}
                              >
                                Excluded
                              </span>
                            ) : isMapped ? (
                              <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">
                                Mapped
                              </span>
                            ) : isUnmappedWithBudget ? (
                              <span className="text-[10px] font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                                Unmapped
                              </span>
                            ) : (
                              <span className="text-[10px] text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BudgetCoaMappingView;
